import React, { useEffect, useState } from "react";
import { BrickWall, Minus, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BrickSeries } from "../../store/slices/bricksSlice";

interface Props {
  brick: BrickSeries;
  rate: number;
  onRateInput(val: number): void;
  onDelete(): void;
}

const STEP = 5;
const clamp = (value: number) => Math.min(100, Math.max(0, value));

// A chosen brick series with its share of the wall in percent.
export const SelectedStoneRow: React.FC<Props> = ({ brick, rate, onRateInput, onDelete }) => {
  const [text, setText] = useState(String(Math.round(rate)));
  useEffect(() => setText(String(Math.round(rate))), [rate]);

  const commit = (value: string) => {
    const parsed = parseInt(value.replace("%", ""), 10);
    if (Number.isNaN(parsed)) setText(String(Math.round(rate)));
    else onRateInput(clamp(parsed));
  };

  return (
    <li className="flex min-w-0 items-center gap-3 py-2">
      {brick.displayThumbnail ? (
        <img
          src={brick.displayThumbnail}
          alt=""
          className="h-10 w-14 shrink-0 rounded-md bg-muted object-cover"
        />
      ) : (
        <span className="flex h-10 w-14 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
          <BrickWall className="size-4" />
        </span>
      )}
      <span className="min-w-0 flex-1 truncate text-sm font-medium">{brick.name}</span>
      <div
        className="flex h-8 items-center rounded-md border shadow-xs"
        role="group"
        aria-label={`Aandeel van ${brick.name}`}
      >
        <button
          type="button"
          className="flex size-8 items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-40"
          onClick={() => onRateInput(clamp(rate - STEP))}
          disabled={rate <= 0}
          aria-label="Minder"
        >
          <Minus className="size-3.5" />
        </button>
        <label className="flex items-center text-sm tabular-nums">
          <span className="sr-only">Aandeel in procent</span>
          <input
            inputMode="numeric"
            className="w-8 bg-transparent text-right outline-none"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={(e) => commit(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && commit(e.currentTarget.value)}
          />
          <span className="text-muted-foreground">%</span>
        </label>
        <button
          type="button"
          className="flex size-8 items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-40"
          onClick={() => onRateInput(clamp(rate + STEP))}
          disabled={rate >= 100}
          aria-label="Meer"
        >
          <Plus className="size-3.5" />
        </button>
      </div>
      <Button
        variant="ghost"
        size="icon-sm"
        className="text-muted-foreground hover:text-destructive"
        onClick={onDelete}
        aria-label={`${brick.name} verwijderen`}
      >
        <X />
      </Button>
    </li>
  );
};
